import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, X, Loader2, Trash2 } from "lucide-react";
import { listTrips, deleteTrip } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function TripHistory({ onClose, onOpenTrip }) {
  const { user, login } = useAuth();
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const data = await listTrips();
        setItems(data);
      } catch {
        toast.error(t("history.load_fail"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const remove = async (id) => {
    try {
      await deleteTrip(id);
      setItems((it) => it.filter((x) => x.trip_id !== id));
      toast.success(t("history.deleted"));
    } catch { toast.error(t("history.delete_fail")); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg bg-white rounded-[1.75rem] shadow-[0_24px_80px_rgba(10,37,64,0.18)] border border-[#EEEEF2] overflow-hidden max-h-[82vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="trip-history-modal"
      >
        {/* Header — mirrors homepage typography */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-[#0A2540] tracking-tight">{t("history.title")}</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="history-close-btn"
            className="p-2 -mr-2 rounded-full hover:bg-[#F5F5F7] transition-colors"
            aria-label={t("common.cancel")}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1 scroll-clean">
          {!user && (
            <div className="text-center py-14 px-6">
              <div className="text-4xl mb-3" aria-hidden="true">🔐</div>
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                {t("history.login_required")}
              </p>
              <button
                onClick={login}
                data-testid="history-login-btn"
                className="chip bg-[#0A2540] hover:bg-[#0A2540]/90 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors"
              >
                {t("topbar.login")}
              </button>
            </div>
          )}

          {user && loading && (
            <div className="flex items-center justify-center py-14 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {user && !loading && items.length === 0 && (
            <div className="text-center py-14 px-6" data-testid="history-empty">
              <div className="text-4xl mb-3" aria-hidden="true">🗺️</div>
              <p className="text-base font-medium text-[#0A2540] mb-2">
                {t("history.empty_title")}
              </p>
              <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">
                {t("history.empty_hint")}
              </p>
            </div>
          )}

          {user && items.map((trip) => (
            <div
              key={trip.trip_id}
              className="flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-[#F7F7FA] transition-colors group"
              data-testid={`trip-${trip.trip_id}`}
            >
              <span className="w-9 h-9 rounded-full bg-[#FFF4CC] text-[#D4AF37] flex items-center justify-center shrink-0">
                <Heart className="w-4 h-4 fill-[#D4AF37]" />
              </span>
              <button className="flex-1 text-left min-w-0" onClick={() => onOpenTrip(trip)}>
                <div className="font-medium text-[#1D1D1F] truncate">{trip.title}</div>
                <div className="text-xs text-gray-500 truncate mt-0.5">
                  {trip.trip?.days} {t("iv.days")}{trip.trip?.party ? ` · ${trip.trip.party}` : ""}
                </div>
              </button>
              <button
                onClick={() => remove(trip.trip_id)}
                className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-white transition-colors opacity-60 group-hover:opacity-100"
                aria-label={t("history.delete_fail")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
